import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import {
  dismissInstallment,
  clearInstallmentAlerts,
  type DueInstallment,
  type InstallmentReminderArm,
} from "@/features/leads/stores/installmentNotificationSlice";
import {
  setCurrentLeadId,
  setLeadCurrentTab,
  setPaymentsDealIdToOpen,
  setPaymentsInstallmentIdToOpen,
} from "@/features/leads/stores/leadFunctionalSlice";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  X,
  Eye,
  Phone,
  MessageCircle,
  IndianRupee,
  CalendarClock,
  Wallet,
  AlertTriangle,
  ChevronRight,
} from "lucide-react";
import { buildTelHref, buildWhatsAppUrl } from "@/constants/countryCodes";

/**
 * "Instalment Due" popup — the EMI sibling of `FollowUpNotifier`.
 *
 * Fires off the server's `installment:due` websocket push. Three arms reach it:
 * a pre-reminder N days out, the due day itself, and a once-per-IST-day nudge
 * while it stays overdue. No client-side timer — see
 * `installmentNotificationSlice` for why that could never work.
 *
 * Mounted in AppLayout (inside the router) so "View Lead" and "Record payment"
 * can navigate.
 */

const ARM_STYLE: Record<
  InstallmentReminderArm,
  { title: string; icon: typeof Wallet; chipBg: string; iconColor: string; badgeBg: string; badgeFg: string }
> = {
  pre: {
    title: "Instalment due soon",
    icon: CalendarClock,
    chipBg: "#edf1ff",
    iconColor: "#335CFF",
    badgeBg: "#edf1ff",
    badgeFg: "#335CFF",
  },
  due: {
    title: "Instalment due today",
    icon: Wallet,
    chipBg: "#fff4ec",
    iconColor: "#ff8447",
    badgeBg: "#fff4ec",
    badgeFg: "#ff8447",
  },
  overdue: {
    title: "Instalment overdue",
    icon: AlertTriangle,
    chipBg: "#fdeaed",
    iconColor: "#da2a46",
    badgeBg: "#fdeaed",
    badgeFg: "#da2a46",
  },
};

/** ₹1,20,000 — Indian digit grouping, no decimals unless there are paise. */
function money(n: number): string {
  const hasPaise = Math.round(n * 100) % 100 !== 0;
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: hasPaise ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

function formatDueDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** The one line under the title that says exactly where this stands. */
function timingLine(item: DueInstallment): string {
  const due = formatDueDate(item.dueDate);
  if (item.arm === "overdue" && item.daysOverdue) {
    return `Due ${due} · ${item.daysOverdue} day${item.daysOverdue > 1 ? "s" : ""} ago`;
  }
  if (item.arm === "pre" && item.daysUntilDue) {
    return `Due ${due} · in ${item.daysUntilDue} day${item.daysUntilDue > 1 ? "s" : ""}`;
  }
  return `Due ${due}`;
}

export default function InstallmentDueNotifier() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const active = useAppSelector((s) => s.installmentNotification.active);
  const queue = useAppSelector((s) => s.installmentNotification.queue);
  /**
   * Two independently-mounted hosts each rendering their own Radix Dialog would
   * stack overlays and neither would be dismissable in an obvious order. The
   * follow-up popup wins the tie and this one waits its turn — the alert stays
   * queued, nothing is lost.
   */
  const followUpActive = useAppSelector((s) => s.followUpNotification.active);


  const handleDismiss = useCallback(() => {
    dispatch(dismissInstallment());
  }, [dispatch]);

  const handleViewLead = useCallback(() => {
    const leadId = active?.leadId;
    dispatch(dismissInstallment());
    if (leadId == null) return;
    // The 16ms defer lets the dialog's close animation eat the click tail
    // before the lead sheet mounts — same reason FollowUpNotifier does it.
    window.setTimeout(() => {
      dispatch(setCurrentLeadId(leadId));
      if (!location.pathname.startsWith("/leads")) navigate("/leads");
    }, 16);
  }, [active, dispatch, location.pathname, navigate]);

  /**
   * Open the Payments tab with THIS instalment preselected in the record-payment
   * form. Order matters: the tab and both deep-link targets must be in the store
   * before `setCurrentLeadId` mounts the drawer, or the Payments tab's mount
   * effect reads them as null and lands on the wrong deal.
   */
  const handleRecordPayment = useCallback(() => {
    if (!active) return;
    const { leadId, childId, installmentId } = active;
    dispatch(clearInstallmentAlerts(installmentId));
    window.setTimeout(() => {
      dispatch(setLeadCurrentTab("Payments"));
      dispatch(setPaymentsDealIdToOpen(String(childId)));
      dispatch(setPaymentsInstallmentIdToOpen(String(installmentId)));
      dispatch(setCurrentLeadId(leadId));
      if (!location.pathname.startsWith("/leads")) navigate("/leads");
    }, 16);
  }, [active, dispatch, location.pathname, navigate]);

  // "+N more" = what the server coalesced into this alert, plus later ticks
  // still queued. queue.length alone reads "+0 more" for someone with 40 due at
  // once, because the server sends those as ONE event.
  const queueCount = (active?.alsoDueCount ?? 0) + queue.length;

  const progressPct = useMemo(() => {
    if (!active || active.planTotalAmount <= 0) return 0;
    return Math.min(
      100,
      Math.round((active.planPaidAmount / active.planTotalAmount) * 100),
    );
  }, [active]);

  if (!active || followUpActive) return null;

  const style = ARM_STYLE[active.arm];
  const ArmIcon = style.icon;
  const waTarget = active.whatsapp || active.phone;

  return (
    <Dialog
      open={!!active}
      onOpenChange={(open) => {
        if (!open) handleDismiss();
      }}
    >
      <DialogContent className="sm:max-w-[400px] p-0 rounded-[20px] border-[#e6e8e7] shadow-[0px_8px_40px_rgba(0,0,0,0.12)] gap-0 [&>button]:hidden">
        {/* Header — the arm is signalled here, in one place, not three */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <div className="flex items-center gap-2">
            <div
              className="size-8 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: style.chipBg }}
            >
              <ArmIcon className="size-3.5" style={{ color: style.iconColor }} />
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <p className="text-[13px] font-medium tracking-[-0.26px] text-[#1e302a]">
                  {style.title}
                </p>
                {queueCount > 0 && (
                  <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-[#FF8447] text-white">
                    +{queueCount} more
                  </span>
                )}
              </div>
              <p className="text-[10px] font-normal text-[#9ba3a0]">
                {timingLine(active)}
              </p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="size-7 rounded-full flex items-center justify-center hover:bg-[#f7f9f8] transition-colors"
            aria-label="Dismiss"
          >
            <X className="size-3.5 text-[#697571]" />
          </button>
        </div>

        {/* Who owes it */}
        <div className="mx-4 p-3 rounded-xl bg-[#f7f9f8] border border-[#e6e8e7]">
          <div className="flex items-center gap-2.5">
            <div className="size-9 rounded-full bg-[#eaf5f1] flex items-center justify-center text-[11px] font-medium text-[#218760] shrink-0">
              {active.leadInitials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium tracking-[-0.26px] text-[#1e302a] truncate">
                {active.leadName}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                {active.stage && (
                  <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-[#edf1ff] text-[#335CFF]">
                    {active.stage}
                  </span>
                )}
                {active.phone && (
                  <span className="text-[10px] font-normal text-[#697571]">
                    {active.phone}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* What is owed — reads like a statement line, deliberately */}
        <div className="mx-4 mt-3 p-3 rounded-xl border border-[#e6e8e7]">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[10px] font-medium text-[#697571] uppercase tracking-[0.04em]">
                Instalment {active.seq} of {active.ofCount}
              </p>
              <p className="text-[22px] font-semibold tracking-[-0.5px] text-[#1e302a] tabular-nums leading-tight mt-0.5">
                {money(active.outstanding)}
              </p>
              {active.paidAmount > 0 && (
                <p className="text-[10px] text-[#697571] mt-0.5 tabular-nums">
                  {money(active.paidAmount)} already paid of {money(active.amount)}
                </p>
              )}
              {active.dealName && (
                <p className="text-[11px] text-[#9ba3a0] truncate mt-0.5">
                  {active.dealName}
                </p>
              )}
            </div>
            <span
              className="text-[9px] font-medium px-2 py-0.5 rounded-full shrink-0"
              style={{ backgroundColor: style.badgeBg, color: style.badgeFg }}
            >
              {active.arm === "overdue"
                ? "Overdue"
                : active.arm === "due"
                  ? "Due today"
                  : "Upcoming"}
            </span>
          </div>

          {/* Plan progress — the "where are we overall" line */}
          <div className="mt-2.5 pt-2.5 border-t border-[#eef1f0]">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] text-[#697571]">
                {active.planPaidCount} of {active.planTotalCount} paid
              </span>
              <span className="text-[10px] text-[#697571] tabular-nums">
                {money(active.planPaidAmount)} / {money(active.planTotalAmount)}
              </span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-[#eef1f0] overflow-hidden">
              <div
                className="h-full rounded-full bg-[#218760] transition-all"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 px-4 pt-3 pb-2">
          <button
            onClick={handleRecordPayment}
            className="flex-1 flex items-center justify-center gap-1.5 h-9 rounded-xl bg-[#218760] hover:bg-[#1a6b4c] disabled:opacity-60 text-white transition-colors"
          >
            <IndianRupee className="size-3.5" />
            <span className="text-[12px] font-medium tracking-[-0.24px]">
              Record payment
            </span>
            <ChevronRight className="size-3" />
          </button>
          <button
            onClick={handleViewLead}
            className="size-9 shrink-0 rounded-xl border border-[#e6e8e7] flex items-center justify-center hover:bg-[#f7f9f8] transition-all"
            aria-label="View lead"
            title="View lead"
          >
            <Eye className="size-3.5 text-[#697571]" />
          </button>
          <button
            onClick={() => {
              // Prefer the lead's dedicated WhatsApp number when it has one;
              // most leads only carry `phone`.
              const url = buildWhatsAppUrl(
                waTarget,
                active.phoneCountryCode ?? undefined,
              );
              if (url) window.open(url, "_blank");
            }}
            disabled={!waTarget}
            className="size-9 shrink-0 rounded-xl border border-[#e6e8e7] flex items-center justify-center hover:bg-[#eaf5f1] hover:border-[#d6ebe3] disabled:opacity-40 transition-all"
            aria-label="WhatsApp"
            title="WhatsApp"
          >
            <MessageCircle className="size-3.5 text-[#25D366]" />
          </button>
          <button
            onClick={() => {
              const href = buildTelHref(
                active.phone,
                active.phoneCountryCode ?? undefined,
              );
              if (href) window.open(href, "_self");
            }}
            disabled={!active.phone}
            className="size-9 shrink-0 rounded-xl border border-[#e6e8e7] flex items-center justify-center hover:bg-[#eaf5f1] hover:border-[#d6ebe3] disabled:opacity-40 transition-all"
            aria-label="Call"
            title="Call"
          >
            <Phone className="size-3.5 text-[#218760]" />
          </button>
        </div>

        <p className="px-4 pb-4 text-[9px] text-[#9ba3a0] text-center">
          Dismissing only closes this reminder — the due date does not change.
        </p>

        {/* No Snooze control here yet, on purpose. Snoozing must set a
            server-side suppress-until on the instalment; a client-side timer
            would be invisible to the sweep and the alert would simply come back
            on the next tick. It lands with that endpoint. */}
      </DialogContent>
    </Dialog>
  );
}
